import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useServerTimer
 * @param {string|null} deadlineAt - ISO deadline from server
 * @param {string|null} serverNow  - server's current time at session start
 * @param {Function}    onExpired  - called when timer hits 0
 */
const useServerTimer = (deadlineAt, serverNow, onExpired) => {
  // Stable client→server clock skew (ms). Captured when serverNow is refreshed.
  const clockSkewRef = useRef(0);

  const calcRemaining = useCallback(() => {
    if (!deadlineAt) return null;
    const estServerNow = Date.now() - clockSkewRef.current;
    return Math.max(0, new Date(deadlineAt).getTime() - estServerNow);
  }, [deadlineAt]);

  const [timeLeftMs, setTimeLeftMs] = useState(null);
  const expiredRef = useRef(false);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (serverNow) {
      clockSkewRef.current = Date.now() - new Date(serverNow).getTime();
    }
    setTimeLeftMs(calcRemaining());
    expiredRef.current = false;
  }, [deadlineAt, serverNow, calcRemaining]);

  useEffect(() => {
    if (timeLeftMs === null) return;
    if (timeLeftMs <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpiredRef.current?.();
      return;
    }
    const tick = setInterval(() => {
      const remaining = calcRemaining();
      setTimeLeftMs(remaining);
      if (remaining <= 0 && !expiredRef.current) {
        expiredRef.current = true;
        clearInterval(tick);
        onExpiredRef.current?.();
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [timeLeftMs, calcRemaining]);

  if (timeLeftMs === null) return { hours: 0, minutes: 0, seconds: 0, timeLeftMs: null, isExpired: false };

  const totalSec = Math.floor(timeLeftMs / 1000);
  return {
    hours: Math.floor(totalSec / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
    timeLeftMs,
    isExpired: timeLeftMs <= 0,
  };
};

export default useServerTimer;
